<template>
  <div class="terminal-layout" :class="[`is-${workspace}`, { 'is-admin': isSettings, 'is-sidebar-collapsed': sidebarCollapsed, 'is-phone-landscape': phoneLandscape, 'is-brand-hidden': brandHidden }]">
    <TerminalHeader
      ref="terminalHeader"
      class="terminal-header"
      workbench
      :can-open-ground="canSwitchToGroundStation"
      :inline-orders="isGroundStation && canViewOrders"
      :mqtt-online="$store.state.app.mqttState"
      :shop="headerShop"
      :shops="shopList"
      :selected-plane="selectedPlane"
      :refreshing="terminalRefreshing"
      @refresh="refreshTerminal"
      @weather-change="currentWeather = $event || {}"
      @open-orders="setWorkbenchTab('orders')"
      @new-order="handleNewOrder"
      @refund-request="handleRefundRequest"
      @open-admin="switchWorkspace('admin')"
      @open-ground="setWorkbenchTab('ground')"
    >
      <template slot="navigation">
      <nav ref="workbenchNav" class="workbench-nav" :aria-label="$t('工作区导航')">
        <UiSlidingIndicator :active-key="isGroundStation ? activeWorkbenchTab : ''" />
        <UiButton variant="text" tone="neutral" class="workbench-nav__item" :class="{ 'is-current': isGroundStation && activeWorkbenchTab === 'ground' }" :aria-pressed="isGroundStation && activeWorkbenchTab === 'ground'" :disabled="!canSwitchToGroundStation" icon="el-icon-monitor" @click="setWorkbenchTab('ground')">{{ $t("我的机队") }}</UiButton>
        <UiButton v-if="canViewRoutes" variant="text" tone="neutral" class="workbench-nav__item" :class="{ 'is-current': isGroundStation && activeWorkbenchTab === 'routes' }" :aria-pressed="isGroundStation && activeWorkbenchTab === 'routes'" :disabled="!canSwitchToGroundStation || videoGuidedLayoutActive" :title="$t(videoGuidedLayoutActive ? '退出视频引导后查看航线' : '')" icon="iconfont icon-feihangluxian" @click="setWorkbenchTab('routes')">{{ $t("航线") }}</UiButton>
        <UiButton v-if="canViewOrders" variant="text" tone="neutral" class="workbench-nav__item" :class="{ 'is-current': isGroundStation && activeWorkbenchTab === 'orders' }" :aria-pressed="isGroundStation && activeWorkbenchTab === 'orders'" :disabled="!canSwitchToGroundStation || videoGuidedLayoutActive" :title="$t(videoGuidedLayoutActive ? '退出视频引导后查看订单' : '')" @click="setWorkbenchTab('orders')"><span class="workbench-order-icon" :class="{ 'is-alerting': workbenchOrderCount > 0 }" aria-hidden="true"><i class="iconfont icon-meiyoudingdan-01"></i><i v-if="workbenchOrderCount > 0" class="iconfont icon-jinjidingdan"></i></span>{{ $t("订单") }}<UiCountBadge :count="workbenchOrderCount" /></UiButton>
        <GroundStationShopSelector v-if="isGroundStation && $store.state.user.is_god" v-model="overviewShopId" :shops="shopList" inline />
      </nav>
      <span ref="workbenchBrand" class="workbench-brand"><span class="workbench-brand__logo" :style="{ '--workbench-logo': `url(${logo})` }" aria-hidden="true"></span><span class="workbench-brand__title">{{ $t($store.state.settings.appName) }}</span></span>
      </template>
    </TerminalHeader>

    <!-- 三个作业页共用左栏；后台继续使用完整权限导航。 -->
    <aside v-if="!isSettings" class="terminal-sidebar" :style="{ '--workbench-enter-x': `${workbenchDirection * 16}px` }">
      <transition name="workbench-tab">
      <GroundStationFleetPanel
        v-if="isGroundStation"
        v-show="activeWorkbenchTab === 'ground'"
        ref="groundStationFleetPanel"
        :planes="groundPlaneList"
        :shops="shopList"
        :is-god="$store.state.user.is_god"
        :selected-plane-id="selectedPlaneId"
        :delivering-plane-ids="deliveringPlaneIds"
        :floating="false"
        :show-operations="false"
        :show-shop-selector="false"
        :collapsed="sidebarCollapsed"
        :suppress-collapsed-popovers="videoGuidedLayoutActive"
        :bottom-limit="groundStationPopoverBottom"
        @select="handlePlaneSelect"
        @focus-route="handleFocusRoute"
      />
      </transition>
      <transition name="workbench-tab">
      <GroundStationRoutePanel ref="routePanel" v-if="isGroundStation && canViewRoutes" v-show="activeWorkbenchTab === 'routes'" :active="routeSelectionActive" :can-assign-route="canAssignRoute" :routes="groundAllRouteList" :bound-route-keys="groundRouteList.map(route => `${route.shop_id}:${route.id}`)" :route-colors="groundRouteColors" @focus-route="handleFocusRoute" />
      </transition>
      <transition name="workbench-tab">
      <OperationalOrderPanel v-if="isGroundStation && canViewOrders && ordersOpened" v-show="activeWorkbenchTab === 'orders'" compact :shop-id="overviewShopId" :show-shop-filter="false" :allow-all-shops="$store.state.user.is_god" :tab-request="orderTabRequest" @close="setWorkbenchTab('ground')" />
      </transition>
      <AdminMenuBar v-if="!isGroundStation" :collapsed="sidebarCollapsed" />
      <!-- 2-2 底部统一承载侧栏折叠入口。 -->
      <div v-if="!isGroundStation && !phoneLandscape && !videoGuidedLayoutActive" class="sidebar-collapse-footer">
        <UiIconControl
          class="sidebar-collapse-control"
          variant="footer"
          :title="$t(sidebarCollapsed ? '展开操作栏' : '折叠操作栏')"
          :icon="['iconfont', sidebarCollapsed ? 'icon-a-yousuojin3x' : 'icon-a-zuosuojin3x']"
          :flashing="sidebarControlFlashing"
          @click="toggleSidebar"
        />
      </div>
    </aside>

    <!-- 右侧保留地图、视频、遥测与原业务路由。 -->
    <section class="terminal-workspace">
      <div v-if="terminalDataUnavailable" class="terminal-network-warning" role="alert">
        <span>{{ $t('终端数据加载失败，请检查网络或服务器后重试') }}</span>
        <UiButton variant="text" tone="warning" :disabled="terminalRefreshing" @click="refreshTerminal">{{ $t('重试') }}</UiButton>
      </div>
      <main class="terminal-main" :class="{ 'is-full-height-map': isFullHeightMapPage }">
        <router-view
          ref="workspacePage"
          :key="workspacePageRefreshKey"
          :current-weather="currentWeather"
          :floating-controls="floatingControls"
          :route-selection-active="routeSelectionActive"
          @plane-select="handlePlaneSelect"
          @route-select="handleMapRouteSelect"
          @route-drop-preview="handleRouteDropPreview"
          @video-guided-layout="handleVideoGuidedLayout"
          @popover-bottom-change="groundStationPopoverBottom = $event"
        />
      </main>
    </section>

    <!-- 与工作区无关的全局飞行安全提示只创建一次。 -->
    <OscillationGuard />
  </div>
</template>

<script>
import { t } from '@/i18n'
import '@/styles/admin-theme.scss'
import '@/styles/ground-station-theme.scss'
import logo from '@/assets/logo.svg'
import AdminMenuBar from '@/components/admin/AdminMenuBar'
import GroundStationFleetPanel from '@/components/ground-station/GroundStationFleetPanel'
import GroundStationRoutePanel from '@/components/ground-station/GroundStationRoutePanel'
import GroundStationShopSelector from '@/components/ground-station/GroundStationShopSelector'
import OperationalOrderPanel from '@/components/OperationalOrderPanel'
import OscillationGuard from '@/components/OscillationGuard'
import TerminalHeader from '@/components/TerminalHeader'
import terminalRuntime from '@/mixins/terminalRuntime'
import mqtt from '@/utils/mqtt'
import { prepareStandaloneRoute, getStandaloneRoute } from '@/utils/api/table'
import { requestMissionReply } from '@/utils/missionSafety'
import { canAccessRouteMeta, hasPermission } from '@/utils/authorization'
import { getOrderStatusGroups } from '@/utils/operationalStatus'
import { getBoundSiteRoutes } from '@/utils/siteRuntime'
import { getGroundStationRouteOverlays } from '@/utils/groundStationRoutes'
import { speakText } from '@/utils/index'
import { openOrderNotification } from '@/utils/uiNotification'
import {
  findAccessibleWorkspaceEntry,
  getRouteWorkspace,
  WORKSPACE_ADMIN,
  WORKSPACE_GROUND_STATION,
  WORKSPACE_SETTINGS
} from '@/utils/workspace'

const PHONE_LANDSCAPE_QUERY = '(max-width: 1024px) and (max-height: 540px) and (orientation: landscape)'

export default {
  name: 'TerminalLayout',
  mixins: [terminalRuntime],
  components: {
    AdminMenuBar,
    GroundStationFleetPanel,
    GroundStationRoutePanel,
    GroundStationShopSelector,
    OperationalOrderPanel,
    OscillationGuard,
    TerminalHeader
  },
  data () {
    return {
      logo,
      brandHidden: false,
      currentWeather: {},
      activeWorkbenchTab: 'ground',
      workbenchDirection: 1,
      ordersOpened: false,
      orderTabRequest: null,
      newOrderNotice: false,
      newOrderInfo: null,
      newOrderNotification: null,
      newOrderPromptShown: false,
      newOrderType: 'pending',
      terminalRefreshing: false,
      routeDropBusy: false,
      workspacePageRefreshKey: 0,
      sidebarControlFlashing: false,
      sidebarControlFlashTimer: null,
      phoneLandscape: window.matchMedia(PHONE_LANDSCAPE_QUERY).matches,
      videoGuidedLayoutActive: false,
      groundStationPopoverBottom: null,
      videoGuidedSidebarSnapshot: null
    }
  },
  computed: {
    workspace () {
      return getRouteWorkspace(this.$route)
    },
    isGroundStation () {
      return this.workspace === WORKSPACE_GROUND_STATION
    },
    routeSelectionActive () {
      return this.isGroundStation && this.activeWorkbenchTab === 'routes' && this.canViewRoutes && !this.videoGuidedLayoutActive
    },
    isSettings () {
      return this.workspace === WORKSPACE_SETTINGS
    },
    floatingControls () {
      return this.isGroundStation
    },
    isFullHeightMapPage () {
      return this.workspace === WORKSPACE_ADMIN && this.$route.meta.fullHeightMap === true
    },
    sidebarCollapsed () {
      if (this.isSettings) return false
      return this.videoGuidedLayoutActive || (!this.isGroundStation && (this.phoneLandscape || this.$store.getters['app/getIsCollapse']))
    },
    canViewRoutes () {
      return this.$store.getters['app/getModuleVisible']('route') && hasPermission(this.$store.state.user, 'terminal.dashboard')
    },
    canAssignRoute () {
      return this.canViewRoutes && hasPermission(this.$store.state.user, 'terminal.dashboard.route.assign')
    },
    canViewOrders () {
      return this.$store.getters['app/getModuleVisible']('order') && hasPermission(this.$store.state.user, 'terminal.dashboard')
    },
    workbenchOrderCount () {
      return getOrderStatusGroups(this.$store.state.paidOrderList, this.overviewShopId).totalCount
    },
    selectedPlaneId () {
      return String(this.$route.query.plane_id || '')
    },
    selectedPlane () {
      return this.airList.find(plane => String(plane.id) === this.selectedPlaneId) || null
    },
    overviewShopId: {
      get () {
        return this.$store.state.user.is_god
          ? String(this.$store.getters['app/getOverviewFleetShopId'] || '')
          : String(this.$store.state.user.shop_id || '')
      },
      set (shopId) {
        if (this.$store.state.user.is_god) this.$store.commit('app/setOverviewFleetShopId', shopId)
      }
    },
    groundAllRouteList () {
      return (this.$store.state.routeList || []).filter(route => !this.overviewShopId || String(route.shop_id) === this.overviewShopId)
    },
    groundRouteList () {
      return getBoundSiteRoutes(this.$store.state.routeList || [], this.$store.state.siteList || [], this.overviewShopId)
    },
    groundRouteColors () {
      return Object.fromEntries(getGroundStationRouteOverlays(this.groundRouteList, this.$store.state.siteList || [], this.groundPlaneList, this.$store.state.paidOrderListLoaded ? this.$store.state.paidOrderList : null).map(route => [route.routeId, route.color]))
    },
    groundPlaneList () {
      return this.airList.filter(plane => (
        String(plane.onoff) === '1' &&
        (!this.overviewShopId || String(plane.shop_id) === this.overviewShopId)
      ))
    },
    deliveringPlaneIds () {
      return (this.$store.state.paidOrderList || [])
        .filter(order => order.shipment_status === '已发货' && order.by_plane_id)
        .map(order => String(order.by_plane_id))
    },
    headerShop () {
      const shopId = this.isGroundStation
        ? this.overviewShopId
        : this.$store.state.user.shop_id
      if (!shopId) return null
      return this.shopList.find(shop => String(shop.shop_id || shop.id) === String(shopId)) || null
    },
    terminalRoutes () {
      const terminalRoute = this.$router.options.routes.find(route => route.meta && route.meta.terminalShell)
      return terminalRoute ? terminalRoute.children : []
    },
    canSwitchToGroundStation () {
      if (!this.$store.getters['app/getModuleVisible']('home')) return false
      const overviewRoute = this.terminalRoutes.find(route => route.path === '/home/index')
      return Boolean(overviewRoute && canAccessRouteMeta(this.$store.state.user, overviewRoute.meta || {}))
    },
    workspaceSwitchDisabledTargets () {
      return this.canSwitchToGroundStation
        ? []
        : [WORKSPACE_GROUND_STATION]
    }
  },
  watch: {
    activeWorkbenchTab (next, previous) {
      const tabs = ['ground', 'routes', 'orders']
      this.workbenchDirection = tabs.indexOf(next) >= tabs.indexOf(previous) ? 1 : -1
    },
    canViewRoutes (allowed) {
      if (!allowed && this.activeWorkbenchTab === 'routes') this.activeWorkbenchTab = 'ground'
    },
    canViewOrders (allowed) {
      if (!allowed && this.activeWorkbenchTab === 'orders') this.activeWorkbenchTab = 'ground'
    },
    videoGuidedLayoutActive (active) { if (!active) this.promptNewOrder() },
    workspace: {
      immediate: true,
      handler (workspace) {
        if (workspace === WORKSPACE_GROUND_STATION) this.$nextTick(() => this.promptNewOrder())
        document.body.classList.toggle('terminal-admin-theme', workspace === WORKSPACE_ADMIN || workspace === WORKSPACE_SETTINGS)
      }
    }
  },
  beforeDestroy () {
    if (this.brandResizeObserver) this.brandResizeObserver.disconnect()
    if (this.brandResizeFrame) cancelAnimationFrame(this.brandResizeFrame)
    window.removeEventListener('resize', this.scheduleBrandSpaceCheck)
    if (this.stopPhoneViewportListener) this.stopPhoneViewportListener()
    this.restoreVideoGuidedSidebarLayout()
    clearTimeout(this.sidebarControlFlashTimer)
    if (this.newOrderNotification) this.newOrderNotification.close()
    document.body.classList.remove('terminal-admin-theme')
  },
  mounted () {
    const media = window.matchMedia(PHONE_LANDSCAPE_QUERY)
    const update = () => { this.phoneLandscape = media.matches }
    if (media.addEventListener) media.addEventListener('change', update)
    else media.addListener(update)
    this.stopPhoneViewportListener = () => {
      if (media.removeEventListener) media.removeEventListener('change', update)
      else media.removeListener(update)
    }
    update()
    this.$nextTick(() => {
      const header = this.$refs.terminalHeader?.$el
      if (!header) return
      this.brandResizeObserver = typeof ResizeObserver === 'function'
        ? new ResizeObserver(this.scheduleBrandSpaceCheck)
        : null
      if (this.brandResizeObserver) {
        [header, this.$refs.workbenchNav, this.$refs.workbenchBrand, header.querySelector('.header-environment'), header.querySelector('.ground-status')]
          .filter(Boolean).forEach(element => this.brandResizeObserver.observe(element))
      }
      window.addEventListener('resize', this.scheduleBrandSpaceCheck)
      this.scheduleBrandSpaceCheck()
    })
  },
  updated () {
    this.scheduleBrandSpaceCheck()
  },
  methods: {
    scheduleBrandSpaceCheck () {
      if (this.brandResizeFrame) return
      this.brandResizeFrame = requestAnimationFrame(() => {
        this.brandResizeFrame = null
        const header = this.$refs.terminalHeader?.$el
        const nav = this.$refs.workbenchNav
        const brand = this.$refs.workbenchBrand
        const environment = header?.querySelector('.header-environment')
        const status = header?.querySelector('.ground-status')
        if (!header || !nav || !brand || !environment || !status) return
        // Measure with the brand visible so a previously hidden brand can return when space opens up.
        this.$el.classList.remove('is-brand-hidden')
        const width = header.clientWidth
        const gap = parseFloat(getComputedStyle(header).columnGap) || 0
        const brandRect = brand.getBoundingClientRect()
        const navRect = nav.getBoundingClientRect()
        const environmentRect = environment.getBoundingClientRect()
        const brandContent = [...brand.children].reduce((sum, child) => sum + child.scrollWidth, 0) + (parseFloat(getComputedStyle(brand).columnGap) || 0)
        const statusRect = status.getBoundingClientRect()
        const hidden = width <= 700
          ? brandRect.right + gap > (environment.getClientRects().length ? environmentRect.left : statusRect.left)
          : width <= 1600
            ? brandRect.width + 1 < brandContent || environment.scrollWidth > environment.clientWidth + 1 || status.scrollWidth > status.clientWidth + 1 || statusRect.right > header.getBoundingClientRect().right + 1
            : brandRect.left < navRect.right + gap || brandRect.right + gap > (environment.getClientRects().length ? environmentRect.left : statusRect.left)
        this.$el.classList.toggle('is-brand-hidden', hidden)
        if (this.brandHidden !== hidden) this.brandHidden = hidden
      })
    },
    handleNewOrder (order) {
      this.newOrderInfo = order
      this.newOrderType = 'pending'
      this.newOrderNotice = !(this.isGroundStation && this.activeWorkbenchTab === 'orders')
      this.newOrderPromptShown = false
      this.promptNewOrder()
    },
    promptNewOrder () {
      if (!this.newOrderNotice || !this.isGroundStation || !this.canViewOrders || this.videoGuidedLayoutActive || this.newOrderPromptShown) return
      if (this.newOrderNotification) this.newOrderNotification.close()
      this.newOrderPromptShown = true
      const type = this.newOrderType
      this.newOrderNotification = openOrderNotification({
        order: this.newOrderInfo,
        type,
        onView: () => {
          this.orderTabRequest = { tab: type === 'refund' ? 'refund' : 'pending' }
          this.setWorkbenchTab('orders')
        }
      })
    },
    handleRefundRequest (order) {
      this.newOrderInfo = order
      this.newOrderType = 'refund'
      this.newOrderNotice = true
      this.newOrderPromptShown = false
      this.promptNewOrder()
    },
    setWorkbenchTab (tab) {
      if (!this.canSwitchToGroundStation) return
      if (this.videoGuidedLayoutActive && tab !== 'ground') return
      if (tab === 'routes' && !this.canViewRoutes) return
      if (tab === 'orders' && !this.canViewOrders) return
      this.activeWorkbenchTab = tab
      if (tab === 'orders') {
        this.ordersOpened = true
        this.newOrderNotice = false
        if (this.newOrderNotification) this.newOrderNotification.close()
        this.newOrderNotification = null
      }
      if (!this.isGroundStation) this.switchWorkspace(WORKSPACE_GROUND_STATION)
    },
    handleVideoGuidedLayout (active) {
      if (active) {
        if (this.videoGuidedLayoutActive) return
        const fleetPanel = this.$refs.groundStationFleetPanel
        const workspacePage = this.$refs.workspacePage
        this.videoGuidedSidebarSnapshot = {
          activeWorkbenchTab: this.activeWorkbenchTab,
          activeTools: fleetPanel && typeof fleetPanel.getCollapsedToolSnapshot === 'function'
            ? fleetPanel.getCollapsedToolSnapshot()
            : [],
          operationPanel: workspacePage && typeof workspacePage.getOperationPanelSnapshot === 'function'
            ? workspacePage.getOperationPanelSnapshot()
            : ''
        }
        this.videoGuidedLayoutActive = true
        this.activeWorkbenchTab = 'ground'
        this.$nextTick(() => {
          const panel = this.$refs.groundStationFleetPanel
          if (panel && typeof panel.closeCollapsedTools === 'function') panel.closeCollapsedTools()
        })
        return
      }
      this.restoreVideoGuidedSidebarLayout()
    },
    restoreVideoGuidedSidebarLayout () {
      if (!this.videoGuidedLayoutActive && !this.videoGuidedSidebarSnapshot) return
      const snapshot = this.videoGuidedSidebarSnapshot
      this.videoGuidedLayoutActive = false
      this.videoGuidedSidebarSnapshot = null
      if (!snapshot) return
      const tab = snapshot.activeWorkbenchTab
      this.activeWorkbenchTab = (tab === 'routes' && this.canViewRoutes) || (tab === 'orders' && this.canViewOrders) ? tab : 'ground'
      this.$nextTick(() => {
        const panel = this.$refs.groundStationFleetPanel
        if (panel && typeof panel.restoreCollapsedToolSnapshot === 'function') {
          panel.restoreCollapsedToolSnapshot(snapshot.activeTools)
        }
        const page = this.$refs.workspacePage
        if (page && typeof page.restoreOperationPanelSnapshot === 'function') {
          page.restoreOperationPanelSnapshot(snapshot.operationPanel)
        }
      })
    },
    switchWorkspace (requestedWorkspace) {
      const targetWorkspace = requestedWorkspace || (this.isGroundStation ? WORKSPACE_ADMIN : WORKSPACE_GROUND_STATION)
      if (targetWorkspace === this.workspace || this.workspaceSwitchDisabledTargets.includes(targetWorkspace)) return
      const target = findAccessibleWorkspaceEntry(
        this.terminalRoutes,
        targetWorkspace,
        meta => canAccessRouteMeta(this.$store.state.user, meta)
      ) || (targetWorkspace === WORKSPACE_ADMIN ? `/admin/edit/${this.$store.state.user.id}` : '/home/index')
      if (target && this.$route.path !== target) this.$router.push({ path: target, query: this.selectedPlaneId ? { plane_id: this.selectedPlaneId } : {} })
    },
    toggleSidebar () {
      if (this.isGroundStation || this.phoneLandscape || this.videoGuidedLayoutActive) return
      this.$store.commit('app/setCollapse')
      clearTimeout(this.sidebarControlFlashTimer)
      this.sidebarControlFlashing = false
      this.$nextTick(() => {
        this.sidebarControlFlashing = true
        this.sidebarControlFlashTimer = setTimeout(() => {
          this.sidebarControlFlashing = false
          this.sidebarControlFlashTimer = null
        }, 660)
      })
    },
    async refreshTerminal () {
      if (this.terminalRefreshing) return
      this.terminalRefreshing = true
      try {
        await this.refreshTerminalData()
        const canInitialize = this.$store.state.user.is_god || (
          this.shop_id && (this.$store.state.user.permissions || []).length > 0
        )
        if (canInitialize) {
          await this.initMqttSubscribe()
          this.refreshPlaneRequest()
        }
        const header = this.$refs.terminalHeader
        if (header && typeof header.refreshHeaderData === 'function') await header.refreshHeaderData()
        await this.refreshCurrentPage()
        speakText('刷新终端数据')
        this.$message.success(t('终端数据已刷新'))
      } catch (error) {
        this.$message.error(t('终端数据刷新失败，请检查网络'))
      } finally {
        this.terminalRefreshing = false
      }
    },
    /**
     * 刷新当前路由页：优先调用页面提供的精准刷新接口；未提供时仅重建路由组件。
     * TerminalLayout 本身不会销毁，因此 MQTT、标题栏和工作区状态不会重新初始化。
     */
    async refreshCurrentPage () {
      const page = this.$refs.workspacePage
      if (page && typeof page.refreshPage === 'function') {
        await page.refreshPage()
        return
      }
      this.workspacePageRefreshKey += 1
      await this.$nextTick()
    },
    /**
     * MQTT数据更新只刷新声明了关联key的当前页面，不重建表单，避免覆盖尚未保存的输入。
     */
    async refreshCurrentPageFromDataUpdate (keys) {
      const pageRefreshKeys = Array.isArray(this.$route.meta.dataRefreshKeys)
        ? this.$route.meta.dataRefreshKeys
        : []
      if (!pageRefreshKeys.some(key => keys.includes(key))) return

      const page = this.$refs.workspacePage
      if (page && typeof page.refreshPage === 'function') await page.refreshPage()
    },
    handlePlaneSelect (planeId) {
      const id = String(planeId || '')
      if (id) this.setWorkbenchTab('ground')
      const query = { ...this.$route.query }
      if (id) query.plane_id = id
      else delete query.plane_id
      if (id !== this.selectedPlaneId) this.$router.replace({ path: this.$route.path, query })
      else if (id && this.$refs.workspacePage && typeof this.$refs.workspacePage.focusSelectedPlaneOnMap === 'function') {
        this.$refs.workspacePage.focusSelectedPlaneOnMap()
      }
    },
    handleMapRouteSelect (routeId) {
      if (!this.isGroundStation || !this.canViewRoutes || this.videoGuidedLayoutActive) return
      this.setWorkbenchTab('routes')
      this.$nextTick(() => {
        if (this.routeSelectionActive && this.$refs.routePanel) this.$refs.routePanel.selectRoute(routeId)
      })
    },
    async handleRouteDropPreview ({ route, plane }) {
      if (!this.routeSelectionActive || !this.canAssignRoute || !route || !plane || this.routeDropBusy) return
      const current = this.groundAllRouteList.find(item => String(item.id) === String(route.id) && String(item.shop_id) === String(route.shopId))
      if (!current) return
      const state = plane.planeState
      if (!state?.online || !state.espOnline || state.isUnlock !== false || Number(state.questState) === 8) {
        this.$message.warning(this.$t('请等待飞机在线、加锁并处于待命状态'))
        return
      }
      if (!mqtt.isConnected()) { this.$message.error(this.$t('指令服务器未连接')); return }
      try {
        const prompt = plane.mission_route_id
          ? '确认将航线「{0}」上传到飞机「{1}」？当前航线将被覆盖。'
          : '确认将航线「{0}」上传到飞机「{1}」？'
        await this.$confirm(this.$t(prompt, [current.name || `航线 ${current.id}`, plane.name || plane.plane_code]), this.$t('上传航线'), { type: 'warning' })
      } catch (error) { return }
      this.routeDropBusy = true
      try {
        const params = { shop_id: plane.shop_id, plane_id: plane.id }
        const task = await prepareStandaloneRoute({ ...params, route_id: current.id })
        await this.$store.dispatch('fetchAirList')
        if (task.plane_code !== plane.plane_code || !plane.planeState?.online || plane.planeState.isUnlock !== false) throw new Error('飞机状态已变化，请重新上传')
        const latest = await getStandaloneRoute(params)
        if (latest.mission_token !== task.mission_token) throw new Error('飞机航线已被其他终端更改')
        const payload = JSON.parse(task.route_data)
        if (!payload.questAss || !Array.isArray(payload.questAss.tasks) || !payload.questAss.tasks.length) throw new Error('航线数据异常')
        payload.questAss.missionToken = task.mission_token
        payload.questAss.missionKind = 'standalone'
        if (!mqtt.publishCommand(plane, JSON.stringify(payload))) throw new Error('航线发送失败')
        let confirmed = false
        for (let attempt = 0; attempt < 8; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 2500))
          const currentTask = await getStandaloneRoute(params)
          if (currentTask.mission_token !== task.mission_token) throw new Error('飞机航线已被其他终端更改')
          const status = await requestMissionReply(plane, 'missionStatusRequest', 'missionHandshake')
          if (status.ready && status.missionToken === task.mission_token && Number(status.questState) === 4) { confirmed = true; break }
          if (status.missionToken && status.missionToken !== task.mission_token && attempt > 2) break
        }
        if (!confirmed) throw new Error('飞机未确认写入航线，请核对状态后重试')
        await this.$store.dispatch('fetchAirList')
        this.$message.success(this.$t('飞机已确认写入航线'))
      } catch (error) {
        this.$message.error(this.$t(error.message || '上传航线失败'))
      } finally {
        this.routeDropBusy = false
      }
    },
    handleFocusRoute (routeData, routeId) {
      const page = this.$refs.workspacePage
      if (page && typeof page.focusRoute === 'function') page.focusRoute(routeData, routeId)
    }
  }
}
</script>

<style lang="scss" scoped>
.terminal-layout {
  --terminal-sidebar-width: 204.8px;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: var(--terminal-sidebar-width) minmax(0, 1fr);
  grid-template-rows: var(--ui-terminal-header-height) minmax(0, 1fr);
  color: var(--ui-text-primary);
  background: var(--ui-bg-page);
  font-family: var(--ui-font-family-sans);
  font-size: var(--ui-font-size-body);
  transition: grid-template-columns .2s ease;
}

.terminal-layout.is-admin {
  --terminal-sidebar-width: 220px;
}

.terminal-layout.is-settings {
  grid-template-columns: minmax(0, 1fr);
}

.terminal-layout.is-settings .terminal-workspace {
  grid-column: 1;
}

.terminal-layout.is-sidebar-collapsed {
  grid-template-columns: var(--gs-collapsed-sidebar-width, 61px) minmax(0, 1fr);
}

.terminal-header { grid-column: 1 / -1; grid-row: 1; min-width: 0; z-index: 130; }
.workbench-nav { position: relative; display: flex; align-items: center; gap: var(--ui-space-xs); flex-shrink: 0; padding-left: var(--ui-space-md); }
.workbench-nav__item::v-deep > i { font-size: 16px; }
.workbench-nav__item::v-deep > i { font-size: 16px; margin-right: var(--ui-space-xs); }
.workbench-nav__item::v-deep > span { display: inline-flex; align-items: center; }
.workbench-brand { position: absolute; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: var(--ui-space-sm); font-size: var(--ui-font-size-section-title); font-weight: 700; white-space: nowrap; }
.workbench-brand__logo { width: 26px; height: 26px; flex-shrink: 0; background: var(--ui-color-primary); -webkit-mask: var(--workbench-logo) center / contain no-repeat; mask: var(--workbench-logo) center / contain no-repeat; }
.workbench-nav__item { position: relative; height: var(--ui-terminal-header-height); border-radius: 0; }
.workbench-nav__item.is-current { color: var(--ui-text-primary); background: var(--ui-bg-elevated); }
.workbench-nav__item .ui-count-badge { margin-left: var(--ui-space-xs); }
.workbench-nav .workbench-order-icon { position: relative; display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; margin-right: var(--ui-space-xs); }
.workbench-order-icon i { position: absolute; inset: 0; font-size: 16px; line-height: 16px; }
.workbench-order-icon.is-alerting i:first-child { animation: workbench-order-normal 1.4s steps(1, end) infinite; }
.workbench-order-icon.is-alerting i:last-child { color: var(--ui-color-warning); animation: workbench-order-alert 1.4s steps(1, end) infinite; }
@keyframes workbench-order-normal { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
@keyframes workbench-order-alert { 0%, 49% { opacity: 0; } 50%, 100% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .workbench-order-icon.is-alerting i:first-child { animation: none; opacity: 0; }
  .workbench-order-icon.is-alerting i:last-child { animation: none; opacity: 1; }
}

.terminal-sidebar {
  grid-column: 1;
  grid-row: 2;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr) 51px;
  overflow: hidden;
  border-right: 1px solid var(--ui-border-color-subtle);
  background: var(--ui-surface-finish), var(--ui-bg-panel);
  box-sizing: border-box;
  transition: background-color var(--ui-duration-normal) ease;
}

.is-ground-station .terminal-sidebar::v-deep .operation-tabs button { min-width: 0; padding: 0 var(--ui-space-xs); font-size: var(--ui-font-size-caption); letter-spacing: 0; }

.sidebar-collapse-footer {
  display: block;
  border-top: 1px solid var(--ui-border-color-subtle);
  background: var(--ui-bg-panel);
}

.sidebar-collapse-control {
  --terminal-control-color: var(--ui-text-primary);
  --terminal-control-hover-color: var(--ui-text-inverse);
  --terminal-control-feedback-color: var(--ui-color-primary);
  --terminal-control-feedback-rgb: var(--ui-color-primary-rgb);
}

.terminal-workspace {
  grid-column: 2;
  grid-row: 2;
  min-width: 0;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
}

.terminal-network-warning {
  position: absolute;
  z-index: 120;
  top: var(--ui-space-md);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: var(--ui-space-sm);
  max-width: calc(100% - 2 * var(--ui-space-md));
  padding: var(--ui-space-xs) var(--ui-space-md);
  border: 1px solid var(--ui-color-warning);
  border-radius: var(--ui-radius-medium);
  color: var(--ui-text-primary);
  background: var(--ui-bg-elevated);
  box-shadow: var(--ui-shadow-floating);
}

.terminal-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--ui-bg-page);
}

/* 后台页面按内容纵向滚动；地面站仍保持固定画布，避免地图和视频产生外层滚动。 */
.terminal-layout.is-admin .terminal-main {
  box-sizing: border-box;
  padding-bottom: var(--ui-space-lg);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.terminal-layout.is-admin .terminal-main.is-full-height-map {
  padding-bottom: 0;
}

.terminal-layout.is-admin .terminal-main::-webkit-scrollbar {
  width: 8px;
}

.terminal-layout.is-admin .terminal-main::-webkit-scrollbar-track {
  background: var(--ui-bg-canvas);
}

.terminal-layout.is-admin .terminal-main::-webkit-scrollbar-thumb {
  border: 2px solid var(--ui-bg-canvas);
  border-radius: 999px;
  background: var(--ui-border-color);
}

.terminal-layout.is-admin .terminal-main::-webkit-scrollbar-thumb:hover {
  background: var(--ui-color-primary-active);
}

.terminal-main > :deep(*) {
  box-sizing: border-box;
}

.is-ground-station .terminal-sidebar,
.is-phone-landscape .terminal-sidebar {
  grid-template-rows: minmax(0, 1fr);
}

.terminal-layout.is-ground-station { --gs-floating-top: 10px; --gs-floating-right: 0px; }
@media (max-width: 1024px) and (max-height: 540px) and (orientation: landscape) {
  .terminal-layout.is-ground-station { --terminal-sidebar-width: 176px; }
  .workbench-nav { gap: 0; }
  .workbench-nav__item { font-size: var(--ui-font-size-caption); padding-left: var(--ui-space-sm); padding-right: var(--ui-space-sm); }
}
@media (max-width: 700px) {
  .terminal-layout.is-ground-station { --terminal-sidebar-width: 160px; }
}
@media (max-width: 1600px) {
  .workbench-brand { position: static; transform: none; flex: 1 1 auto; min-width: 0; justify-content: center; font-size: var(--ui-font-size-body); }
  .workbench-brand__logo { width: 22px; height: 22px; }
  .workbench-nav__item { padding-left: var(--ui-space-sm); padding-right: var(--ui-space-sm); }
}
.terminal-layout.is-brand-hidden .workbench-brand { display: none; }
@media (max-width: 700px) {
  .terminal-layout { --ui-terminal-header-height: 88px; }
  .workbench-brand { position: absolute; top: 8px; left: 50%; transform: translateX(-50%); height: 26px; }
  .workbench-nav { align-self: flex-end; height: 48px; }
  .workbench-nav__item { height: 48px; }
  .terminal-header::v-deep .ground-status { align-self: flex-end; margin-bottom: 2px; }
}
</style>
